import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import Alert from '../../ui/Alert'
import FormWrapper from '../../ui/form/FormWrapper'
import Modal from '../../ui/Modal'
import { ChannelType, Subscription } from '../../types'
import TextInput from '../../ui/form/TextInput'
import Heading from '../../ui/Heading'
import { SearchIcon } from '../../ui/icons'
import { useTranslation } from 'react-i18next'
import SwitchField from '../../ui/form/SwitchField'
import TopicChannelSection from './TopicChannelSection'
import { groupTopicChannels, loadTopics } from './topics'
import './Subscriptions.css'

type TopicEditing = Partial<Subscription> & Pick<Subscription, 'channel'>

export default function Subscriptions() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [topics, setTopics] = useState<null | Subscription[]>(null)
    const [failed, setFailed] = useState(false)
    const [editing, setEditing] = useState<null | TopicEditing>(null)
    const [search, setSearch] = useState('')

    const reload = useCallback(async () => {
        setFailed(false)
        try {
            setTopics(await loadTopics(project.id))
        } catch (error) {
            console.error(error)
            setTopics(null)
            setFailed(true)
        }
    }, [project])

    useEffect(() => {
        reload().catch(error => console.error(error))
    }, [reload])

    const sections = useMemo(() => groupTopicChannels(topics ?? [], search), [topics, search])
    const modalTitle = editing?.id
        ? t('update_subscription')
        : t('create_channel_subscription', { channel: editing ? t(editing.channel) : '' })

    return (
        <>
            <Heading
                size="h2"
                title={t('topics')}
                actions={
                    <div className="topics-search">
                        <TextInput
                            name="search"
                            value={search}
                            placeholder={t('search_topics')}
                            onChange={setSearch}
                            hideLabel={true}
                            icon={<SearchIcon />}
                        />
                    </div>
                }
            />
            {failed
                ? <Alert variant="error" title={t('error')}>{t('topics_load_error')}</Alert>
                : sections.map(section => (
                    <TopicChannelSection
                        key={section.channel}
                        {...section}
                        isLoading={topics === null}
                        onAdd={(channel: ChannelType) => setEditing({ channel })}
                        onEdit={setEditing}
                    />
                ))}
            <Modal
                title={modalTitle}
                open={Boolean(editing)}
                onClose={() => setEditing(null)}
            >
                {editing && <FormWrapper<Pick<Subscription, 'id' | 'name' | 'is_public' | 'is_opt_in'>>
                    onSubmit={async ({ id, name, is_public, is_opt_in }) => {
                        if (id) {
                            await api.subscriptions.update(project.id, id, editing.is_channel ? { name } : { name, is_public })
                        } else {
                            await api.subscriptions.create(project.id, { name, channel: editing.channel, is_public, is_opt_in })
                        }
                        await reload()
                        setEditing(null)
                    }}
                    defaultValues={editing}
                >
                    {
                        form => (
                            <>
                                <TextInput.Field
                                    form={form}
                                    name="name"
                                    required
                                    label={t('name')}
                                />
                                {!editing.is_channel && <SwitchField
                                    form={form}
                                    name="is_public"
                                    subtitle={t('public_desc')}
                                    label={t('public')}
                                />}
                                {editing.id
                                    ? <SwitchField
                                        name="is_opt_in"
                                        checked={Boolean(editing.is_opt_in)}
                                        disabled
                                        subtitle={t('opt_in_locked_desc')}
                                        label={t('opt_in')}
                                    />
                                    : <SwitchField
                                        form={form}
                                        name="is_opt_in"
                                        subtitle={t('opt_in_desc')}
                                        label={t('opt_in')}
                                    />}
                            </>
                        )
                    }
                </FormWrapper>}
            </Modal>
        </>
    )
}
